import { useEffect, useState } from "react";
import { Gift, Loader2, Mail, UserX } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import WhatsAppLink from "@/components/admin/WhatsAppLink";
import { paymentReminder, trialInvite } from "@/lib/waTemplates";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const TRIAL = { none: ["Trial not started", "bg-yellow-300 text-slate-900"], active: ["Trial running", "bg-green-500"], ended: ["Trial used", "bg-slate-600"],
  grade_used: ["Grade already trialled", "bg-slate-600"], former: ["Former subscriber", "bg-slate-600"], paid: ["Paid", "bg-green-700"] };

export default function UnpaidParents() {
  const [rows, setRows] = useState(null);
  const [onlyDemo, setOnlyDemo] = useState(false);
  const [onlyTrial, setOnlyTrial] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/unpaid").then(({ data }) => setRows(data.parents)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loader2 className="animate-spin mx-auto" />;
  let list = onlyDemo ? rows.filter((r) => r.finished_demo) : rows;
  if (onlyTrial) list = list.filter((r) => r.never_paid);
  const toInvite = rows.filter((r) => r.never_paid && !r.blocked && !r.trial_invite_sent_at);
  const emailAll = async () => {
    if (!window.confirm(`Send the 10-min free trial email to ${toInvite.length} parent(s) who never paid and were not invited yet?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/trial-invites/email", {});
      toast.success(`Sending the trial email to ${data.count} parent(s).`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-unpaid">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="bg-slate-800 rounded-2xl border-2 border-rose-400/60 px-4 py-2 flex items-center gap-2" data-testid="unpaid-count">
          <UserX size={18} className="text-rose-300" /> <span className="font-bold">{list.length}</span>
          <span className="text-slate-300 text-sm">parent{list.length === 1 ? "" : "s"} without paid access</span>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={onlyTrial} onChange={(e) => setOnlyTrial(e.target.checked)} className="w-4 h-4 accent-yellow-300" data-testid="unpaid-only-never-paid" />
          Only parents who never paid (10-min trial)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={onlyDemo} onChange={(e) => setOnlyDemo(e.target.checked)} className="w-4 h-4 accent-yellow-300" data-testid="unpaid-only-demo" />
          Only parents whose child finished the old demo
        </label>
        <button onClick={emailAll} disabled={busy || !toInvite.length} data-testid="unpaid-trial-email-all"
          className="ml-auto rounded-full bg-yellow-300 text-slate-900 font-bold px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Email the 10-min trial to {toInvite.length} parent{toInvite.length === 1 ? "" : "s"}
        </button>
      </div>
      {!list.length ? <p className="text-slate-400 text-center py-8" data-testid="unpaid-empty">No unpaid parents.</p> : (
        <div className="space-y-2">{list.map((p) => <UnpaidRow key={p.id} p={p} />)}</div>
      )}
    </div>
  );
}

function UnpaidRow({ p }) {
  return (
    <div className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-3 flex flex-wrap items-start gap-x-6 gap-y-2" data-testid={`unpaid-row-${p.id}`}>
      <div className="min-w-[220px]">
        <p className="font-bold">{p.name} {p.blocked && <span className="text-xs text-rose-300">(blocked)</span>}</p>
        <p className="text-xs text-slate-400">{p.email}</p>
        <p className="text-xs text-slate-400">Signed up {fmt(p.created_at)}{p.reminder_sent_at ? ` · reminder email sent ${fmt(p.reminder_sent_at)}` : ""}</p>
        {p.trial_invite_sent_at && <p className="text-xs text-yellow-200" data-testid={`unpaid-trial-invited-${p.id}`}><Gift size={11} className="inline" /> Trial email sent {fmt(p.trial_invite_sent_at)}</p>}
      </div>
      <div className="flex-1 min-w-[220px] text-sm space-y-1">
        {!p.children.length ? <span className="text-slate-500">No child added yet</span> : p.children.map((c, i) => (
          <p key={i} className="flex flex-wrap items-center gap-2">{c.name} (G{c.grade})
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TRIAL[c.trial]?.[1] || "bg-slate-600"}`} data-testid={`unpaid-trial-${p.id}-${i}`}>{TRIAL[c.trial]?.[0] || c.trial}</span>
          </p>
        ))}
      </div>
      <div className="flex flex-col gap-1.5">
        {p.never_paid && <WhatsAppLink number={p.whatsapp} name={p.name} message={trialInvite(p.name, p.children)} messageLabel="Send 10-min trial invite" consentAccount={p.consent_account} consentMarketing={p.consent_marketing} testId={`unpaid-trial-whatsapp-${p.id}`} />}
        <WhatsAppLink number={p.whatsapp} name={p.name} message={paymentReminder(p.name, p.children)} messageLabel="Send payment reminder" consentAccount={p.consent_account} consentMarketing={p.consent_marketing} testId={`unpaid-whatsapp-${p.id}`} />
      </div>
    </div>
  );
}
